import React from "react";
import { toast } from "sonner";
import { shipClientEvent } from "../lib/clientLog";

/**
 * Top-level ErrorBoundary so one bad render in any page doesn't crash the
 * whole app to a white screen. Renders a friendly fallback with a reload
 * button, the full error message, and the React component stack — so the
 * user can screenshot it and we can pinpoint the exact failing component
 * (especially for `Objects are not valid as a React child` errors where
 * the offending object's keys are needed to find the bad render).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught:", error, info?.componentStack);
    this.setState({ info });
    try { toast.error(`Page crashed: ${String(error?.message || error)}`); } catch {}
    try {
      shipClientEvent({
        source: "boundary",
        message: String(error?.message || error),
        stack: (error?.stack || "") + "\n--componentStack--\n" + (info?.componentStack || ""),
      });
    } catch {}
  }
  reset = () => {
    this.setState({ error: null, info: null });
  };
  reload = () => {
    window.location.reload();
  };
  copyDetails = async () => {
    const { error, info } = this.state;
    const text = [
      `URL: ${window.location.href}`,
      `Time: ${new Date().toISOString()}`,
      "",
      `Message: ${String(error?.message || error)}`,
      "",
      "Stack:",
      error?.stack || "(none)",
      "",
      "Component stack:",
      info?.componentStack || "(none)",
    ].join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Error details copied — paste into chat");
    } catch {
      toast.error("Clipboard blocked — select the text below and copy manually");
    }
  };
  render() {
    if (!this.state.error) return this.props.children;
    const message = String(this.state.error?.message || this.state.error);
    const componentStack = this.state.info?.componentStack || "";
    const stack = this.state.error?.stack || "";
    // Pull the first 1-3 component names off the stack so they're visible
    // even on small screenshots — this is what we needed for the Yesterday
    // crash ("which component rendered the bad object?").
    const topComponents = (componentStack || "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 5);
    return (
      <div className="min-h-screen flex items-start justify-center bg-white p-6 overflow-y-auto" data-testid="error-boundary-fallback">
        <div className="max-w-2xl w-full border border-red-200 bg-red-50 rounded-lg p-6 space-y-4 my-6">
          <div className="text-lg font-semibold text-red-800">Something broke on this page</div>

          <div className="text-xs font-mono bg-white border border-red-200 rounded p-3 text-red-700 break-all whitespace-pre-wrap">
            {message}
          </div>

          {topComponents.length > 0 && (
            <div className="text-xs">
              <div className="font-semibold text-slate-700 mb-1">Failing component path</div>
              <div className="bg-white border border-slate-200 rounded p-3 font-mono text-[11px] text-slate-700 space-y-0.5">
                {topComponents.map((line, i) => (
                  <div key={i} className={i === 0 ? "font-semibold text-red-700" : ""}>{line}</div>
                ))}
              </div>
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Full stack trace</summary>
            <pre className="mt-2 bg-white border border-slate-200 rounded p-3 font-mono text-[10px] text-slate-700 overflow-x-auto whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
{stack}
{"\n--componentStack--\n"}{componentStack}
            </pre>
          </details>

          <div className="text-[12px] text-slate-600">
            Your data is safe. Click <b>Reload</b> to recover, or <b>Copy details</b> and paste into chat so we can ship a fix fast.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={this.reload}
              data-testid="error-boundary-reload"
              className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded px-3 py-1.5"
            >Reload</button>
            <button
              onClick={this.copyDetails}
              data-testid="error-boundary-copy"
              className="bg-slate-800 hover:bg-slate-900 text-white text-sm font-medium rounded px-3 py-1.5"
            >Copy details</button>
            <a
              href="/"
              onClick={(e) => { e.preventDefault(); this.reset(); window.location.href = "/"; }}
              data-testid="error-boundary-home"
              className="text-sm font-medium text-slate-700 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50"
            >Go home</a>
          </div>
        </div>
      </div>
    );
  }
}
